import { useEffect, useState } from 'react'
import { Sparkles, Plus, Download, Search, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSolutionRequirements } from '@/hooks/useSolutionRequirements'
import type { GeneratingTier } from '@/hooks/useSolutionRequirements'
import { GenerateStep } from './shared'
import { VisionTree } from './VisionTree'
import { PrdTree } from './PrdTree'
import { SpecsTree } from './SpecsTree'
import type { RequirementLens } from '@ispot/shared'

interface Props {
  accountId: string
  projectId: string
}

const LENS_TABS: { id: RequirementLens; label: string }[] = [
  { id: 'vision', label: 'Vision' },
  { id: 'prd', label: 'PRD' },
  { id: 'specs', label: 'Specs' },
]

const TIER_GENERATE_LABELS: Record<RequirementLens, string> = {
  vision: 'Generate Vision',
  prd: 'Generate PRD',
  specs: 'Generate Specs',
}

export function SolutionConfiguratorPage({ accountId, projectId }: Props) {
  const {
    tree, isLoading, isGenerating, generatingTier, error, activeLens, setActiveLens,
    loadTree, ensureDefault, generateFromGraph, generateVision, generatePrd, generateSpecs, exportMarkdown,
  } = useSolutionRequirements(accountId, projectId)

  const [search, setSearch] = useState('')
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const [expandedDetail, setExpandedDetail] = useState<string | null>(null)

  useEffect(() => { ensureDefault() }, [ensureDefault])

  const toggleSection = (key: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      return next
    })
  }

  const handleExport = async () => {
    const md = await exportMarkdown(activeLens)
    const blob = new Blob([md], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `${activeLens}-${Date.now()}.md`; a.click()
    URL.revokeObjectURL(url)
  }

  const handleTierGenerate = () => {
    if (activeLens === 'vision') generateVision()
    else if (activeLens === 'prd') generatePrd()
    else generateSpecs()
  }

  const isEmpty = !tree || tree.capabilities.length === 0
  const totalFeatures = tree?.capabilities.reduce((sum, c) => sum + c.features.length, 0) || 0
  const treeProps = { tree: tree!, search, expandedSections, toggleSection, expandedDetail, setExpandedDetail }

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-[1100px] mx-auto px-9 py-7">
        {/* Header */}
        <div className="flex items-start justify-between mb-1">
          <div>
            <h1 className="font-display text-[23px] font-bold tracking-tight">Solution Configurator</h1>
            <p className="text-sm text-text-muted mt-0.5">Three-tier document generation from the knowledge graph</p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleExport} className="flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-md border border-border bg-white hover:bg-warm-100 transition-colors">
              <Download className="w-3.5 h-3.5" /> Export
            </button>
            <button className="flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-md bg-warm-900 text-white hover:bg-warm-800 transition-colors">
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
            <button onClick={generateFromGraph} disabled={isGenerating} className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-md bg-solution text-white hover:bg-solution-dark transition-colors disabled:opacity-60">
              <Sparkles className="w-3.5 h-3.5" />
              {isGenerating ? 'Generating...' : 'Generate from Research'}
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mt-4 mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" />
          <input type="text" placeholder="Search items..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm rounded-md border border-border bg-white placeholder:text-warm-500 focus:outline-none focus:ring-1 focus:ring-solution/30" />
        </div>

        {/* Lens Tabs with per-tab regenerate */}
        <div className="flex items-center gap-0 border-b border-border mb-0">
          {LENS_TABS.map((tab) => (
            <button key={tab.id} onClick={() => { setActiveLens(tab.id); setExpandedSections(new Set()); setExpandedDetail(null) }}
              className={cn('px-5 py-2 text-sm font-medium -mb-px border-b-2 transition-colors',
                activeLens === tab.id ? 'text-solution border-solution' : 'text-text-muted border-transparent hover:text-text-secondary')}>
              {tab.label}
              <span className={cn('ml-1.5 text-[10px] font-semibold px-1.5 py-px rounded-full',
                activeLens === tab.id ? 'bg-solution/15 text-solution' : 'bg-warm-200 text-text-muted')}>
                {totalFeatures}
              </span>
            </button>
          ))}
          {!isEmpty && !isGenerating && (
            <button
              onClick={handleTierGenerate}
              className="ml-auto flex items-center gap-1 px-2.5 py-1 text-[12px] font-medium text-text-muted hover:text-solution rounded transition-colors"
              title={TIER_GENERATE_LABELS[activeLens]}
            >
              <RefreshCw className="w-3 h-3" />
              {TIER_GENERATE_LABELS[activeLens]}
            </button>
          )}
        </div>

        {/* States */}
        {error && <div className="mt-4 px-4 py-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-100">{error}</div>}
        {isLoading && <div className="mt-8 text-center text-text-muted text-sm">Loading requirements...</div>}

        {isGenerating && <GenerationProgress tier={generatingTier} />}

        {!isLoading && !isGenerating && isEmpty && (
          <div className="mt-8 text-center py-16 bg-white border border-border rounded-lg">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-solution/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-solution" />
            </div>
            <h3 className="font-display text-base font-semibold text-text-secondary mb-1">Generate from Research</h3>
            <p className="text-sm text-text-muted mb-5 max-w-md mx-auto">
              Your evidence from interviews and documents will be structured into capabilities and features
            </p>
            <button onClick={generateFromGraph} className="px-5 py-2 text-sm font-medium rounded-md bg-solution text-white hover:bg-solution-dark transition-colors">
              <Sparkles className="w-3.5 h-3.5 inline mr-1.5" /> Generate from Research
            </button>
            <p className="text-xs text-text-muted mt-3">Or add requirements manually</p>
          </div>
        )}

        {/* Treegrid */}
        {!isLoading && !isGenerating && !isEmpty && tree && (
          <div className="bg-white border border-border border-t-0 rounded-b-lg overflow-hidden">
            {activeLens === 'vision' && <VisionTree {...treeProps} />}
            {activeLens === 'prd' && <PrdTree {...treeProps} />}
            {activeLens === 'specs' && <SpecsTree {...treeProps} />}
          </div>
        )}
      </div>
    </div>
  )
}

function GenerationProgress({ tier }: { tier: GeneratingTier }) {
  if (tier === 'vision') {
    return (
      <div className="mt-8 bg-white border border-border rounded-lg p-6">
        <div className="flex flex-col gap-3 text-sm">
          <GenerateStep label="Reading knowledge graph..." done />
          <GenerateStep label="Generating product vision and capabilities..." done={false} active />
        </div>
      </div>
    )
  }
  if (tier === 'prd') {
    return (
      <div className="mt-8 bg-white border border-border rounded-lg p-6">
        <div className="flex flex-col gap-3 text-sm">
          <GenerateStep label="Vision ready" done />
          <GenerateStep label="Generating requirements with user stories and acceptance criteria..." done={false} active />
        </div>
      </div>
    )
  }
  if (tier === 'specs') {
    return (
      <div className="mt-8 bg-white border border-border rounded-lg p-6">
        <div className="flex flex-col gap-3 text-sm">
          <GenerateStep label="Vision ready" done />
          <GenerateStep label="Requirements ready" done />
          <GenerateStep label="Generating technical specifications..." done={false} active />
        </div>
      </div>
    )
  }
  // tier === 'all'
  return (
    <div className="mt-8 bg-white border border-border rounded-lg p-6">
      <div className="flex flex-col gap-3 text-sm">
        <GenerateStep label="Reading knowledge graph..." done />
        <GenerateStep label="Generating product vision..." done={false} active />
        <GenerateStep label="Generating requirements..." done={false} />
        <GenerateStep label="Generating technical specs..." done={false} />
      </div>
    </div>
  )
}
